import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, TextField, Button as MuiButton, Checkbox, FormControlLabel, Typography, Grid, Divider, Alert, Link as MuiLink } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../stores/store';
import { saveUserDetails, generateUserId } from '../../features/games/bicycleGameSlice';
import barcode from '../../assets/images/bicycle_game/barcode-aztec-crem.png'
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers';
import theme from '../../styles/themes/default';

interface GamePopupProps {
  isOpen: boolean;
  onClose: () => void;
  score: number;
}

// Convert makeStyles to styled components
const PopupOverlay = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  paddingTop: '2rem',
});

const PopupContent = styled('div')({
  backgroundColor: '#4a3824',
  padding: '2rem',
  borderRadius: '4px',
  border: '2px dotted #e9d4a8',
  width: '80%',
  maxWidth: '900px',
  maxHeight: '85vh',
  overflowY: 'auto'
});

const FormTextField = styled(TextField)({
  backgroundColor: '#f5e8c9',
  borderRadius: '4px',
  '& .MuiInputBase-input': {
    textAlign: 'center',
    fontWeight: 'bold',
    color: '#4a3824',
    padding: '12px 14px',
  },
  '& input::placeholder': {
    color: '#4a3824',
    textAlign: 'center',
    fontWeight: 'bold',
    opacity: 1
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  }
});

const ActionButton = styled(MuiButton)({
  backgroundColor: '#f5e8c9',
  color: '#4a3824',
  fontWeight: 'bold',
  padding: '10px 20px',
  fontSize: '1.2rem',
  '&:hover': {
    backgroundColor: '#ffffff',
  },
  borderRadius: '4px',
  boxShadow: 'none',
  textTransform: 'uppercase'
});

const GamePopup: React.FC<GamePopupProps> = ({ isOpen, onClose, score }) => {
  const dispatch = useAppDispatch();
  const { user, error: apiError } = useAppSelector((state) => state.bicycleGame);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    birthDate: null as Dayjs | null,
    phoneNumber: '',
    agree: false,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const validateFields = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.birthDate) newErrors.birthDate = 'Data nașterii este obligatorie';
    if (!formData.lastName) newErrors.lastName = 'Numele este obligatoriu';
    if (!formData.firstName) newErrors.firstName = 'Prenumele este obligatoriu';
    if (!formData.phoneNumber) newErrors.phoneNumber = 'Numărul de telefon este obligatoriu';
    if (!formData.agree) newErrors.agree = 'Trebuie să acceptati regulamentul';
    
    // Phone validation
    if (formData.phoneNumber && formData.phoneNumber.length !== 10) {
      newErrors.phoneNumber = 'Numărul de telefon trebuie să fie de 10 cifre';
    }
    
    // Birth date validation for minimum age
    if (formData.birthDate) {
      const birthDate = formData.birthDate.toDate();
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (age < 18 || (age === 18 && monthDiff < 0) || (age === 18 && monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        newErrors.birthDate = 'Trebuie să aveti cel putin 18 ani';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitSuccess(false);
    setSubmitError(null);

    if (!validateFields()) {
      setSubmitError('Vă rugăm să completati toate câmpurile obligatorii corect.');
      return;
    }

    try {
      // Check if a userId already exists in localStorage or Redux store
      const existingUserId = localStorage.getItem('bicycleGameUserId') || user;
      let userId = existingUserId;
      
      // If no userId exists, generate a new one
      if (!userId) {
          userId = await dispatch(generateUserId()).unwrap();
      }
      
      // Make sure userId is not null at this point
      if (!userId) {
          throw new Error('Could not generate or retrieve user ID');
      }
      
      // Save user details with the existing or newly generated userId
      const result = await dispatch(saveUserDetails({
          unique_id: userId as string,
          firstName: formData.firstName || null,
          lastName: formData.lastName || null,
          birthDate: formData.birthDate ? formData.birthDate.format('YYYY-MM-DD') : null,
          phoneNumber: formData.phoneNumber || null
      })).unwrap();

      // Store userId in localStorage
      localStorage.setItem('bicycleGameUserId', userId);

      setSubmitSuccess(true);
      
      // Close popup after a short delay to show success message
      setTimeout(() => {
        handleClose();
      }, 5000);
      
    } catch (error: any) {
      console.error('Error saving user details:', error);
      
      // Verificăm dacă eroarea vine cu un mesaj specific din API
      if (error && typeof error === 'object') {
        // Dacă există un mesaj de eroare în răspunsul API
        if (error.message) {
          setSubmitError(error.message);
        } 
        // Dacă există erori de validare pentru câmpuri specifice
        else if (error.errors && typeof error.errors === 'object') {
          const fieldErrors: { [key: string]: string } = {};
          Object.entries(error.errors).forEach(([key, value]) => {
            fieldErrors[key.toLowerCase()] = Array.isArray(value) ? value[0] : String(value);
          });
          setErrors(prev => ({ ...prev, ...fieldErrors }));
          setSubmitError('Există erori de validare. Vă rugăm să verificati formatul datelor introduse.');
        } 
        // Pentru alte tipuri de erori
        else {
          setSubmitError('A apărut o eroare la salvarea datelor. Vă rugăm să încercati din nou.');
        }
      } else {
        setSubmitError('A apărut o eroare la salvarea datelor. Vă rugăm să încercati din nou.');
      }
    }
  };
  
  const handleSkip = async () => {
    setSubmitError(null);
    try {
      // Check if userId already exists in localStorage or Redux store
      let userId = localStorage.getItem('bicycleGameUserId') || user;
      
      // If no userId exists, generate a new one
      if (!userId) {
          userId = await dispatch(generateUserId()).unwrap();
      }
      
      // Make sure userId is not null before proceeding
      if (!userId) {
          throw new Error('Could not generate or retrieve user ID');
      }
      
      // Store userId in localStorage
      localStorage.setItem('bicycleGameUserId', userId as string);
      
      handleClose();
    } catch (error) {
      console.error('Error starting game:', error);
      setSubmitError('A apărut o eroare la initierea jocului. Vă rugăm să încercati din nou.');
    }
  };

  if (!isOpen) return null;

  const handleClose = () => {
    // Resume game activity
    const iframe = document.querySelector('iframe');
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage({ 
        type: 'resumeGame',
        action: 'unpause'
      }, '*');
    }
    onClose();
  };

  const handleBirthdateChange = (newDate: Dayjs | null) => {
      setFormData(prev => ({ ...prev, birthDate: newDate }));
      setIsDatePickerOpen(false);
    };
  
    const handleBirthdateFocus = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDatePickerOpen(true);
    };

  const handleDownloadPdf = () => {

    const pdfUrl = `/pdf/qr kauf.pdf`;
    
    // Create an anchor element and set properties for download
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Kozel_Kaufland_Voucher.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <PopupOverlay>
      <PopupContent>
        <Grid container spacing={4} sx={{ mt: 2, justifyContent: 'center', alignItems: 'center' }}>
          {/* Left Side - Game Info & Voucher */}
          

          {/* Right Side - Form */}
          <Grid item xs={12} md={6}>
            <Box component="form" onSubmit={handleSubmit} sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2
            }}>
              <Typography variant="h4" sx={{ 
                textAlign: 'center', 
                color: '#e9d4a8',
                mb: -1,
                textTransform: 'uppercase',
                fontFamily: theme.fonts.poplar
              }}>
                Vrei să participi în tragerea la sorți
              </Typography>

              {submitSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Felicitări! Datele tale au fost înregistrate cu succes.
                </Alert>
              )}

              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submitError}
                </Alert>
              )}
              
              <Typography variant="h5" sx={{ 
                textAlign: 'center', 
                fontWeight: 'normal', 
                color: '#e9d4a8',
                mb: 1
              }}>
               pentru 3x biciclete electrice?
              </Typography>

              <Typography variant="h5" sx={{ 
                textAlign: 'center', 
                fontWeight: 'normal', 
                color: '#e9d4a8',
              }}>
               Completează formularul
              </Typography>
              
              <DatePicker
                  label="Data nașterii*"
                  value={formData.birthDate}
                  onAccept={handleBirthdateChange}
                  format="DD/MM/YYYY"
                  open={isDatePickerOpen}
                  onOpen={() => setIsDatePickerOpen(true)}
                  onClose={() => setIsDatePickerOpen(false)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.birthDate,
                      helperText: errors.birthDate,
                      onClick: handleBirthdateFocus,
                      sx: {
                        backgroundColor: '#f5e8c9',
                        borderRadius: '4px',
                        '& .MuiInputBase-input': {
                          textAlign: 'center',
                          fontWeight: 'bold',
                          color: '#4a3824',
                          padding: '12px 14px',
                        },
                        '& .MuiInputLabel-root': {
                          color: '#4a3824',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          left: 0,
                          right: 0,
                          marginLeft: 'auto',
                          marginRight: 'auto',
                          width: 'fit-content',
                          transform: 'translate(0, 8px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            opacity: 0
                          }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '& .MuiFormHelperText-root': {
                          color: '#ff1744',
                          margin: '4px 0 0',
                          fontSize: '12px',
                          textAlign: 'center'
                        }
                      }
                    }
                  }}
                />

              <FormTextField
                placeholder="Nume"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                required
                error={!!errors.lastName}
                helperText={errors.lastName}
                FormHelperTextProps={{
                  sx: {
                    color: '#ff1744',
                    margin: '4px 0 0',
                    fontSize: '12px',
                    textAlign: 'center'
                  }
                }}
              />

              <FormTextField
                placeholder="Prenume"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                required
                error={!!errors.firstName}
                helperText={errors.firstName}
                FormHelperTextProps={{
                  sx: {
                    color: '#ff1744',
                    margin: '4px 0 0',
                    fontSize: '12px',
                    textAlign: 'center'
                  }
                }}
              />

              <FormTextField
                placeholder="Număr de telefon"
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => {
                  // Only allow digits
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  if (value.length <= 10) {
                    setFormData(prev => ({ ...prev, phoneNumber: value }))
                  }
                }}
                required
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                FormHelperTextProps={{
                  sx: {
                    color: '#ff1744',
                    margin: '4px 0 0',
                    fontSize: '12px',
                    textAlign: 'center'
                  }
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.agree}
                    onChange={(e) => setFormData(prev => ({ ...prev, agree: e.target.checked }))}
                    required
                    sx={{
                      color: '#e9d4a8',
                      '&.Mui-checked': {
                        color: '#e9d4a8',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ 
                    fontSize: '14px', 
                    lineHeight: 1.2, 
                    color: '#e9d4a8' 
                  }}>
                    Am citit integral, am înteles și sunt de acord cu prevederile{' '}
                   <span style={{
                    fontStyle: 'italic',
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                  }}>
                    <MuiLink 
                    href="/pdf/2025/Regulament autentificat_Kozel @ online.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#e9d4a8',
                      fontStyle: 'italic',
                      textDecoration: 'underline',
                      fontWeight: 'bold',
                      '&:hover': {
                        color: '#ffffff'
                      }
                    }}
                  >
                    Regulamentului Campaniei
                  </MuiLink>
                  </span>
                  </Typography>
                }
                sx={{ 
                  '& .MuiFormControlLabel-asterisk': {
                    display: 'none'
                  }
                }}
              />
              {errors.agree && (
                <Typography color="error" sx={{ fontSize: '12px', textAlign: 'center', mt: -1 }}>
                  {errors.agree}
                </Typography>
              )}

              <ActionButton
                type="submit"
                sx={{
                  width: '180px',
                  alignSelf: 'center',
                }}
              >
                Particip
              </ActionButton>
              
              <Divider sx={{ backgroundColor: '#e9d4a8', opacity: 0.3 }} />
              
              <ActionButton
                onClick={handleSkip}
                sx={{
                  backgroundColor: '#e9d4a8',
                  color: '#4a3824',
                  margin: '0 auto',
                  textTransform: 'none'
                }}
              >
                Vreau doar să pedalez
              </ActionButton>
            </Box>
          </Grid>
        </Grid>
      </PopupContent>
    </PopupOverlay>
  );
};

export default GamePopup;